import { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import Block from '@mui/icons-material/Block'
import { Box, Chip, Dialog, DialogContent, DialogProps, Divider, Grid, Skeleton, Tab, Tabs, Typography } from '@mui/material'

import { useGetTrackedEventQuery } from '../../../../api/core'
import { GRBanner } from '../../../../components/GRBanner/GRBanner'
import GRDialogTitle, { GRDialogCloseButton } from '../../../../components/GRDialogTitle/GRDialogTitle'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { useError } from '../../../../hooks/useError'
import languageService from '../../../../services/LanguageService'
import { useInternalAccessCheck } from '../../../account/hooks/roleHooks'
import { GameCardContent } from '../../../game/components/GameCard/GameCard'
import { useRouteModal } from '../../../route-modals/hooks/useRouteModal'
import { useLiveEventArchetypeName } from '../../hooks/liveEventsArchetypes'
import { useEventDialogDuration } from '../../hooks/useEventDialogDuration'
import { EventDialogTab, useEventDialogTabs } from '../../hooks/useEventDialogTabs'
import { useLiveEventTagGroupWithEventTypeId } from '../../hooks/useLiveEventTagGroups'
import { useLiveEventTypeModal } from '../../hooks/useLiveEventTypeModal'
import { useLiveEventDialogAnalyticsEvents } from '../../hooks/useLiveEventsTrackerAnalyticsEvents'
import { LiveEventTagGroup } from '../../types/LiveEventTagGroup'
import { TrackedGame } from '../../types/TrackedGame'
import { TrackingEvent, TrackingEventByGame } from '../../types/TrackingEvents'
import { LiveEventsNotPublishedBadge } from '../LiveEventsNotPublishedBadge/LiveEventsNotPublishedBadge'
import { AllEventsTab } from './AllEventsTab/AllEventsTab'
import { DescriptionTab } from './DescriptionTab/DescriptionTab'
import { EvolutionTab } from './EvolutionTab/EvolutionTab'
import { InstancesTab } from './InstancesTab/InstancesTab'
import './LiveEventsEventDialog.scss'
import { OtherGamesTab } from './OtherGamesTab/OtherGamesTab'

/**
 * Displays a dialog with tabs for browsing single event details, all events related to a game and other games related to
 * the selected event type
 */
type LiveEventsEventDialogProps = DialogProps & {
  data: LiveEventsEventDialogData
  children?: ReactNode
  onTrackedGameChange: (trackedGame: TrackedGame) => void
  onEventChange: (event: TrackingEvent) => void
  onTabChange: (tab: EventDialogTab) => void
  onScreenshotIndexChange: (screenshotIndex: number) => void
}

export type LiveEventsEventDialogData = {
  eventId?: string
  eventTypeId?: string
  trackedGame?: TrackedGame
  durationStart?: number
  durationEnd?: number
  tab?: EventDialogTab
  highlightedEventId?: string
  screenshotIndex?: number
  trackedEvents: TrackingEventByGame
  trackedGames?: TrackedGame[]
}

export const LiveEventsEventDialog: FC<LiveEventsEventDialogProps> = ({
  open,
  onClose,
  onTrackedGameChange,
  onEventChange,
  onTabChange,
  onScreenshotIndexChange,
  data: { eventId, eventTypeId, trackedGame, durationStart, durationEnd, tab, screenshotIndex, trackedEvents, trackedGames },
  children,
}) => {
  const eventQuery = useGetTrackedEventQuery(eventId as string, { skip: !eventId })
  useError({ error: eventQuery.error })

  const { popAllModals } = useRouteModal()
  const tabOptions = useEventDialogTabs()
  const resolvedEventTypeId = !!eventId ? eventQuery.data?.event.typeId : eventTypeId
  const tagName = resolvedEventTypeId && languageService.getTranslation('tags', resolvedEventTypeId)
  const tagGroup = useLiveEventTagGroupWithEventTypeId(resolvedEventTypeId || '')
  const duration = useEventDialogDuration({ durations: eventQuery.data?.durations, durationStart, durationEnd })
  const { showModal: showEventTypeModal } = useLiveEventTypeModal()
  const archetypeId = eventQuery.data?.event.archetypeEvolutionData?.archetypeId
  const archetypeName = useLiveEventArchetypeName({ archetypeId, trackedGame })
  const isDiscontinued = useMemo(() => {
    return trackedGame?.archetypes?.find((archetype) => archetype.archetypeId === archetypeId)?.discontinued
  }, [archetypeId, trackedGame?.archetypes])
  const inactive = eventQuery.data?.event.active === false
  const { t } = useTranslation()

  const dialogContentRef = useRef<HTMLElement>(null)
  useLiveEventDialogAnalyticsEvents(
    trackedGame?.game?.names['us'] || trackedGame?.game?.name,
    trackedGame?.game?.appId,
    eventId,
    tagName,
    resolvedEventTypeId,
    resolvedEventTypeId ? languageService.getTranslation('tags', resolvedEventTypeId) : '',
    tab
  )

  const switchTab = useCallback(
    (tab: EventDialogTab) => {
      onTabChange(tab)
    },
    [onTabChange]
  )

  const handleOnTrackedGameClick = (trackedGame: TrackedGame) => {
    popAllModals()
    showEventTypeModal({ trackedGameId: trackedGame.game.id, eventTypeId: resolvedEventTypeId || eventTypeId || '', tab: EventDialogTab.AllEvents })
  }

  const handleEventChange = (event: TrackingEvent) => {
    switchTab(EventDialogTab.Description)
    onEventChange(event)
  }

  const handleEventHighlighted = (eventId: string) => {
    popAllModals()
  }

  useEffect(() => {
    if (tagGroup && tagGroup.id === LiveEventTagGroup.DEFINING_OCCURRENCES && tab !== EventDialogTab.Description) {
      switchTab(EventDialogTab.Description)
    }
  }, [tagGroup, tab, switchTab])

  const isInternalUser = useInternalAccessCheck()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableRestoreFocus
      className="LiveEventsEventDialog"
      PaperProps={{ sx: { minHeight: '90vh', maxHeight: '100vh' } }}
    >
      <GRDialogTitle>
        <Grid container alignItems="center">
          <Grid item xs={3}>
            {trackedGame && (
              <div className="LiveEventsEventDialog__GameCardContent">
                <GameCardContent game={trackedGame.game} displayGameType discontinued={trackedGame.discontinued} />
                {!trackedGame.published && <LiveEventsNotPublishedBadge />}
              </div>
            )}
          </Grid>
          <Grid item xs={6} container direction="column" alignItems="center">
            {eventQuery.isLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                <Skeleton variant="rounded" width={200} height={40} sx={{ borderRadius: '25px' }} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h3" align="center">
                  {eventQuery.data?.event.name || tagName || t('common:not_available_shorthand')}
                </Typography>
                {inactive && <Chip label={t('live-events:event_inactive')} color="warning" size="small" sx={{ ml: 1, mb: 0, mt: 0, mr: 0 }} />}
              </Box>
            )}
            {eventQuery.data?.event.name && (
              <Typography variant="h4" marginTop={1}>
                {tagName}
              </Typography>
            )}
            {/* TODO: Remove internal user check when we want to release reskins to all users */}
            {archetypeName && isInternalUser && (
              <Chip
                label={archetypeName}
                icon={
                  isDiscontinued ? (
                    <GRTooltip content={t('live-events:live_event_evolution_type_discontinued')} styles={{ height: '20px', width: '20px' }}>
                      <Block color="error" fontSize="small" />
                    </GRTooltip>
                  ) : undefined
                }
                variant="outlined"
                size="small"
                style={{ marginTop: '6px', marginLeft: '0', marginRight: '0', marginBottom: '0' }}
              />
            )}
          </Grid>
          <Grid item xs={3} container direction="column" alignItems="flex-end">
            <GRDialogCloseButton onClick={onClose} share />
          </Grid>
        </Grid>
      </GRDialogTitle>
      <Divider />

      {tagGroup && tagGroup.id !== LiveEventTagGroup.DEFINING_OCCURRENCES && (
        <Tabs value={tab || EventDialogTab.Description}>
          {tabOptions.map((tab) => {
            const disabled = [EventDialogTab.Description, EventDialogTab.Instances, EventDialogTab.LiveEventEvolution].includes(tab.id) && !eventId
            return (
              <Tab
                icon={tab.icon}
                iconPosition="start"
                key={tab.id}
                label={
                  disabled && tab.tooltip ? (
                    <GRTooltip content={tab.tooltip}>
                      <>{tab.label}</>
                    </GRTooltip>
                  ) : (
                    tab.label
                  )
                }
                onClick={() => !disabled && switchTab(tab.id)}
                value={tab.id}
                sx={{ minHeight: 56 }}
                style={{ pointerEvents: 'auto' }}
                disabled={disabled}
                component="div"
              />
            )
          })}
        </Tabs>
      )}

      <DialogContent ref={dialogContentRef} style={{ overflowY: 'scroll' }}>
        {eventQuery.isError && !eventQuery.isFetching ? (
          <GRBanner severity="guidance">
            <Trans i18nKey="live-events:event_not_available" />
          </GRBanner>
        ) : (
          <>
            {tab === EventDialogTab.Description && (
              <DescriptionTab
                currentTrackedGame={trackedGame}
                eventId={eventId}
                eventTypeId={resolvedEventTypeId}
                eventTypeName={tagName}
                screenshotIndex={screenshotIndex}
                duration={duration}
                onScreenshotIndexChange={onScreenshotIndexChange}
                onScrollParentTop={() => dialogContentRef && dialogContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                onEventHighlighted={handleEventHighlighted}
              />
            )}
            {tab === EventDialogTab.Instances && (
              <InstancesTab
                eventId={eventId}
                eventTypeId={resolvedEventTypeId}
                currentTrackedGame={trackedGame}
                duration={duration}
                onEventHighlighted={handleEventHighlighted}
                trackedEvents={trackedEvents}
                trackedGames={trackedGames}
              />
            )}
            {tab === EventDialogTab.AllEvents && (
              <AllEventsTab
                currentTrackedGame={trackedGame}
                currentEventTypeId={resolvedEventTypeId}
                currentEventId={eventId}
                onEventSelect={handleEventChange}
              />
            )}
            {tab === EventDialogTab.OtherGames && (
              <OtherGamesTab eventTypeId={resolvedEventTypeId} currentTrackedGame={trackedGame} onTrackedGameClick={handleOnTrackedGameClick} />
            )}
            {tab === EventDialogTab.LiveEventEvolution && isInternalUser && (
              <EvolutionTab
                currentEventId={eventId}
                currentTrackedGame={trackedGame}
                onEventSelect={handleEventChange}
                onScrollParentTop={() => dialogContentRef && dialogContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
